import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { MobileUser } from '../../domain/models/mobile-user.model';
import { MobileUserCreateInput } from '../../domain/models/mobile-user-create-input.model';
import { MobileUserUpdateInput } from '../../domain/models/mobile-user-update-input.model';
import { IMobileUsersRepository } from '../../domain/repositories/mobile-users.repository';
import { MobileUsersRemoteDatasource } from '../datasources/remote/mobile-users.remote-datasource';
import { MobileUserMapper } from '../mappers/mobile-user.mapper';

@Injectable()
export class MobileUsersRepositoryImpl implements IMobileUsersRepository {
  private readonly datasource = inject(MobileUsersRemoteDatasource);
  private readonly mapper = inject(MobileUserMapper);

  public list(organizationId: number): Observable<readonly MobileUser[]> {
    return this.datasource.list(organizationId).pipe(
      map((dtos) => dtos
        .map((dto) => this.mapper.toMobileUser(dto))
        .filter((user): user is MobileUser => user !== null)),
    );
  }

  public create(organizationId: number, input: MobileUserCreateInput): Observable<MobileUser> {
    const payload = this.mapper.toCreatePayload(input);
    return this.datasource.create(organizationId, payload).pipe(
      map((dto) => this.toMobileUserOrThrow(dto)),
    );
  }

  public update(organizationId: number, id: number, input: MobileUserUpdateInput): Observable<MobileUser> {
    const payload = this.mapper.toUpdatePayload(input);
    return this.datasource.update(organizationId, id, payload).pipe(
      map((dto) => this.toMobileUserOrThrow(dto)),
    );
  }

  public deactivate(organizationId: number, id: number): Observable<void> {
    return this.datasource.deactivate(organizationId, id);
  }

  public resendInvitation(organizationId: number, id: number): Observable<void> {
    return this.datasource.resendInvitation(organizationId, id);
  }

  private toMobileUserOrThrow(dto: Parameters<MobileUserMapper['toMobileUser']>[0]): MobileUser {
    const user = this.mapper.toMobileUser(dto);
    if (!user) {
      throw new Error(`Unexpected non-client role in mobile user response: ${dto.Role}`);
    }
    return user;
  }
}
